import React, { useContext } from 'react'
import Provider from './Provider'
import { useTheme } from '@material-ui/core/styles'
import { Button } from 'components'
import { Box, Grid, Typography } from '@material-ui/core'
import { School, AvatarSelect, PointrThemedPlacesAutocomplete, Input, AsyncResponsesLoading } from 'components'
import { BasicsContext, BasicsContextProvider } from './Provider'
import AlgoliaPlacesProvider, {
  AlgoliaPlacesContext,
  AlgoliaPlacesContextProvider
} from 'smart-components/algolia-places/Provider'
import FileUploadProvider, { FileUploadContext, FileUploadContextProvider } from 'smart-components/file-upload/Provider'
import useStyles from './Styles'
import { IPlaceOption } from 'types'
import { IProps } from './Interface'
import { useMediaQuery } from '@material-ui/core'
import classNames from 'classnames/bind'
import handleEnterKey from 'utils/handle-enter-key'

const BasicsPage: React.FC<IProps> = ({ disabled }) => {
  const { state, handlers } = useContext<BasicsContext>(BasicsContextProvider)
  const { state: algoliaPlacesState, handlers: algoliaPlacesHandlers } = useContext<AlgoliaPlacesContext>(
    AlgoliaPlacesContextProvider
  )
  const { state: fileUploadState, handlers: fileUploadHandlers } = useContext<FileUploadContext>(
    FileUploadContextProvider
  )
  const theme = useTheme()
  const classes = useStyles({})
  const cx = classNames.bind(classes)
  const downMD = useMediaQuery(theme.breakpoints.down('md'))
  const downSM = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Grid container className={classes.root} direction={downSM ? 'column-reverse' : downMD ? 'row-reverse' : 'row'}>
      <Grid
        md={8}
        item={true}
        className={cx({
          infoContainer: true,
          infoContainerMobile: downSM || downMD
        })}
      >
        <Box className={classes.row}>
          <Typography id="basics-my-hometown-label">My hometown is</Typography>
          <PointrThemedPlacesAutocomplete
            id="basics-my-hometown-answer"
            value={state.user.hometownLocation}
            className={classes.hometownAutocomplete}
            options={algoliaPlacesState.options as IPlaceOption[]}
            isLoading={algoliaPlacesState.isLoading}
            searchTerm={algoliaPlacesState.searchTerm}
            placeholder=""
            alwaysEnabled={false}
            onItemChanged={(item: IPlaceOption) =>
              handlers.handleUpdateResponse({ questionKey: 'hometownLocation', response: item })
            }
            onInputChanged={(value: string) => algoliaPlacesHandlers.onSearchTermChanged(value)}
            disabled={disabled}
            autoFocus={Boolean(!state.user.hometownLocation?.city)}
            onKeyPress={handleEnterKey()}
          />
        </Box>
        <Box className={classes.row}>
          <Typography id="basics-currently-working-as-label">Currently working as</Typography>
          <Input
            id="basics-currently-working-as-answer"
            limit={state.questions?.currentRole?.rules?.maxLength}
            showLimit={true}
            autoFocus={false}
            textColor={theme.palette.common.lightBlue}
            alwaysEnabled={false}
            disabled={disabled}
            initialValue={state.user.currentRole}
            onBlur={e => handlers.handleUpdateResponse({ questionKey: 'currentRole', response: e.target.value })}
            onChanged={value => handlers.handleChangeResponse({ questionKey: 'currentRole', response: value })}
            onKeyPress={handleEnterKey((value: any) =>
              handlers.handleUpdateResponse({ questionKey: 'currentRole', response: value })
            )}
          />
        </Box>

        <Box className={classes.row}>
          <Typography id="basics-current-company-label">for the company</Typography>
          <Input
            id="basics-current-company-answer"
            limit={state.questions?.currentCompany?.rules?.maxLength}
            showLimit={true}
            autoFocus={false}
            textColor={theme.palette.common.lightPurple}
            alwaysEnabled={false}
            disabled={disabled}
            initialValue={state.user.currentCompany}
            onBlur={e => handlers.handleUpdateResponse({ questionKey: 'currentCompany', response: e.target.value })}
            onChanged={value => handlers.handleChangeResponse({ questionKey: 'currentCompany', response: value })}
            onKeyPress={handleEnterKey((value: any) =>
              handlers.handleUpdateResponse({ questionKey: 'currentCompany', response: value })
            )}
          />
        </Box>

        <Box className={classes.schoolContainer}>
          {state.user.schools.map((school, i) => (
            <School
              mt={3}
              id={school.schoolId}
              key={`${school.schoolId}-${i}`}
              year={school.gradYear}
              field={school.fieldOfStudy}
              degreeType={school.degreeType || ''}
              rules={state.questions?.schools?.rules}
              selectedSchool={{ label: school.schoolName || '', value: school.schoolId || '' }}
              schoolOptions={state.schools.options}
              loadingSearch={state.schools.isSearching}
              disabled={disabled}
              onSave={() => handlers.handleUpdateSchool(i)}
              onChange={schoolUpdates => handlers.handleChangeSchool({ index: i, schoolUpdates })}
              onSchoolSearch={handlers.handleSearchSchool}
              onSchoolRemove={e => handlers.handleRemoveSchool(e, i)}
              onAddNewSchool={name => handlers.handleAddNewSchool(i, name)}
            />
          ))}
        </Box>

        <Box mt={3} hidden={disabled}>
          <Button
            id="basics-add-school-button"
            textTransform="none"
            onClick={handlers.handleAddSchool}
            disabled={state.user.schools?.length >= state.questions?.schools?.rules?.maxItems}
          >
            <Typography id="basics-add-school-button-label">+ Add more school entries</Typography>
          </Button>
        </Box>
      </Grid>
      <Grid direction="column" md={4} item={true} container>
        <Grid className={classes.userNameContainer}>
          <Typography id="basics-user-name-label" className={classes.userName}>
            {state.user.firstName} {state.user.lastName}
          </Typography>
        </Grid>
        <AvatarSelect
          user={{
            firstName: state.user.firstName,
            lastName: state.user.lastName,
            avatar: fileUploadState.file || state.user.avatar
          }}
          onAdd={e => fileUploadHandlers.onFileSelected(e.target.files[0])}
          onRemove={fileUploadHandlers.onFileRemoved}
          inProgress={fileUploadState.inProgress}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  )
}

export default React.memo((props: IProps) => (
  <FileUploadProvider>
    <AlgoliaPlacesProvider>
      <AsyncResponsesLoading>
        <Provider {...props}>
          <BasicsPage {...props} />
        </Provider>
      </AsyncResponsesLoading>
    </AlgoliaPlacesProvider>
  </FileUploadProvider>
))
